<template>
  <div>
    <section
      class="section-bg rds-lazy py-space-lg py-md-space-xxl"
      data-background-image="https://cms.asuonline.asu.edu/sites/default/files/2021-08/NetworkV02-01.png"
      aria-labelledby="tuition-calculator-title"
    >
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-10 offset-lg-1">
            <CardInfoHorizontal
              bg-image-source="https://cms.asuonline.asu.edu/sites/default/files/cost-image.jpg"
              image-fit="cover"
              bg-image-alt="Two people, one in an ASU maroon graduation cap and gown with a flower lei, standing outdoors near palm trees."
              image-position="left"
              class="tuition-card"
              border-variant="light-4"
              title=""
              :display-label="false"
              :display-highlight="false"
              :cta-as-button="true"
              cta-button-variant="secondary"
              cta-text-variant="dark-3"
              cta-text="Estimate tuition and fees"
              cta-text-size="small"
              cta-text-weight="bold"
              cta-link="#"
              cta-link-target="_self"
              @cta-click="showProgramCostModal"
            >
              <h3
                id="tuition-calculator-title"
                class="mb-space-sm mb-lg-space-sm h3-large text-dark-3"
                tabindex="0"
              >
                {{ title }}
              </h3>
              <div class="mb-space-sm" tabindex="0" v-html="text"></div>
            </CardInfoHorizontal>
          </div>
        </div>
      </div>
    </section>
    <rds-modal
      :visible="showModal"
      bg-variant="light-3"
      :expand-height="true"
      :remove-padding="true"
      @hidden="showModal = false"
      @close="handleModalClose"
    >
      <div>
        <ClientOnly>
          <program-cost
            ref="programCostRef"
            :program-category="programCategory"
            :program-code="programCode"
            :is-mounted-modal="showModal"
            :cost-override="costOverride"
            :cost-academic-year="costAcademicYear"
            :latest-academic-year="latestAcademicYear"
            :fee-code-resident="feeCodeResident"
            :fee-code-non-resident="feeCodeNonResident"
            :cost-collection="costCollection"
            :degree-type="degreeType"
          ></program-cost>
        </ClientOnly>
      </div>
    </rds-modal>
  </div>
</template>

<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { RdsModal } from "@rds-vue-ui/rds-modal";
import ProgramCost from "~/components/degree-page/ProgramCost.vue";
/*
 ********************************************
 * Props
 ********************************************
 */

type Props = {
  title: string;
  programCategory: string;
  costOverride: boolean;
  costAcademicYear: string;
  latestAcademicYear: string;
  programCode: string;
  feeCodeResident: string;
  feeCodeNonResident: string;
  degreeType: "Graduate" | "Undergraduate" | "Certificate";
  costCollection: {
    amount: number;
    summary: string | null;
  } | null;
  text?: string;
};
const props = defineProps<Props>();
/*
 ********************************************
 * data
 ********************************************
 */
const showModal = ref(false);
const programCostRef = ref<InstanceType<typeof ProgramCost>>();
/*
 ********************************************
 * Methods
 ********************************************
 */
const showProgramCostModal = async (e: Event) => {
  e.preventDefault();
  showModal.value = true;
  // fires fetchAll function to make API calls from tuition API on modal open button click.
  await programCostRef.value?.fetchCostApi();
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    "tuition calculator",
    "estimate tuition and fees"
  );
};
const handleModalClose = () => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    "main content",
    props.degreeType === "Certificate"
      ? "the cost per credit hour for this program is:"
      : "your tuition estimate",
    "close cross"
  );
};
</script>

<style scoped>
.section-bg {
  background-size: auto;
  background-position: top;
  background-repeat: repeat;
}

.tuition-card {
  max-width: 920px;
  margin-inline: auto;
}

.tuition-card :deep(p:last-child) {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .tuition-card :deep(.bg-image.image-fit-cover) {
    min-height: 0;
  }
}

@media (min-width: 992px) {
  .tuition-card :deep(.cih-body) {
    padding: 3rem !important;
  }
}
</style>
